import React, { useEffect, useState, useMemo } from 'react';
import { useSingleEffect } from 'react-haiku';
import { useCollect } from 'hooks';
import { toLower } from 'lodash';

import Show from 'components/Show';
import Button from 'components/Ui/Button';
import Progress from 'components/Misc/Progress';
import { Title } from 'components/Misc/Typography';

import Search from 'components/Messenger/Search';
import Nothing from 'components/Messenger/Conversations/Nothing';
import Conversation from 'components/Messenger/Conversations/Conversation';

import { useAppStore } from 'store/useAppStore';
import { useMessengerStore } from 'store/useMessengerStore';

const Conversations = () => {
	// Use app's store
	const user = useAppStore(state => state.user);

	// Use messenger's store
	const [conversations, onInit, updateView] = useMessengerStore(state => [
		state.conversations,
		state.onInit,
		state.updateView
	]);

	// Store search parameters
	const [search, setSearch] = useState({
		name: '',
		role: ''
	});

	// Store collected conversations
	const [[results, progress], collect] = useCollect('conversations');

	// Filter out conversations based on search phrase
	const filtered = useMemo(() => {
		if (!conversations.length) return [];

		return conversations?.filter(conversation => {
			return conversation.type === 'private'
				? toLower(conversation.participants.find(p => p.id !== user.id).fullName).includes(toLower(search.name))
				&& (search.role === '' ? true : search.role.split(',').includes(conversation.participants.find(p => p.id !== user.id).role))
				: toLower(conversation.title).includes(toLower(search.name));
		});
	}, [search, conversations, user]);

	// Handle search parameter change
	const onChange = (event) => {
		const { name, value } = event.target || event;
		setSearch(prev => ({
			...prev,
			[name]: value
		}));
	};

	// Handle view change to contacts
	const onContacts = () => updateView('contacts');

	// Collect conversations on mount
	useSingleEffect(() => {
		(async () => await collect())();
	});

	// Initialize messenger's state
	useEffect(() => {
		if (results) onInit(results);
	}, [results]);

	return (
		<div className="flex flex-col h-full max-h-full">
			{/* Conversation's view header */}
			<div className="px-3 h-12 flex items-center space-x-3 bg-neutral-100 dark:bg-neutral-700 transition-colors">
				<Title
					title="Sarakstes"
					className="flex-1"
				/>

				<Button
					small
					square
					icon="group"
					onClick={onContacts}
				/>
			</div>

			{/* Conversation search */}
			<Search search={search} onChange={onChange} />

			{/* Show loading progress */}
			<Progress progress={progress && !conversations?.length} />

			{/* Show nothing in not progress and no convos */}
			<Show if={!progress && !conversations?.length}>
				<Nothing />
			</Show>

			{/* Conversation list */}
			<Show if={conversations?.length}>
				<div className="p-1 flex-1 overflow-y-auto">
					{filtered?.map(conversation =>
						<Conversation key={conversation.id} conversation={conversation} />
					)}
				</div>
			</Show>
		</div>
	);
};

export default Conversations;
